import React, { ElementType, FC, memo, ButtonHTMLAttributes, useState } from 'react'
// redux
import { Link } from 'react-router-dom'
// types
import { Classes } from 'jss'
import { SvgType } from 'components/svgComponent/Svg'
// styles
import { sheet } from './sheet'
import injectSheet from 'react-jss'
// utils
import { cssVarColors, cssVarLinears, cssVarTextColors } from 'lib/cssSelector'
const { white, gray, lightGray, darkGray, dark, black } = cssVarTextColors
const { main, red } = cssVarColors
const { reward } = cssVarLinears

export type ButtonSizeType = 'sm' | 'md' | 'lg'
export type ButtonVariantType = 'text' | 'contained' | 'outlined' | 'icon'
export type ButtonColorType = 'primary' | 'second' | 'danger' | 'gradientReward' | 'success'
export type ButtonFigureType = 'rounded' | 'roundedSquere'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  disabled?: boolean
  fill?: boolean
  to?: string
  label?: string
  classes: Classes
  svg?: React.SFC<React.SVGProps<SVGSVGElement>>
  Icon?: (props: SvgType) => JSX.Element
  iconActive?: boolean
  size?: ButtonSizeType
  color?: ButtonColorType
  variant?: ButtonVariantType
  figure?: ButtonFigureType
  optionsStyle?: {
    variant: { text?: { addPaddings?: boolean }; icon?: { showBg?: boolean; addPadding?: boolean } }
  }
}

const COLORS = {
  primary: {
    main,
    text: white,
    hover: '#0A3DA4',
    hoverText: main
  },
  second: {
    main: lightGray,
    text: darkGray,
    hover: gray,
    hoverText: dark
  },
  danger: {
    main: red,
    text: white,
    hover: '#BD0D0D',
    hoverText: '#BD0D0D'
  },
  gradientReward: {
    main: `linear-gradient(90deg, ${reward[0]} 0%, ${reward[1]} 100%)`,
    text: white,
    hover: `linear-gradient(90deg, ${reward[0]} 0%, ${reward[1]} 100%)`,
    hoverText: white
  },
  success: {
    main: '#166534',
    text: white,
    hover: '#14532d',
    hoverText: '#166534'
  }
}

export const buttonStyleSelector = (
  color: ButtonColorType,
  variant: ButtonVariantType,
  options: Pick<Props, 'optionsStyle'>
) => {
  const c = COLORS[color]

  const borderStr = '1px solid'

  const isGradient = c.main.startsWith('linear-gradient')

  const base = {}

  const additionalOptionsVariant =
    options.optionsStyle && options.optionsStyle.variant && options.optionsStyle.variant

  const iconAdditionalOptions = additionalOptionsVariant && additionalOptionsVariant.icon
  const textAdditionalOptions = additionalOptionsVariant && additionalOptionsVariant.text

  const variants = {
    contained: {
      color: c.text,
      ...(isGradient
        ? { background: c.main, border: 'none' }
        : { backgroundColor: c.main, border: `${borderStr} ${c.main}` })
    },
    outlined: {
      color: color !== 'second' ? c.main : c.text,
      backgroundColor: 'transparent',
      border: `${borderStr} ${c.main}`
    },
    text: {
      color: color !== 'second' ? c.main : c.text,
      backgroundColor: 'transparent',
      border: 'none',
      padding: textAdditionalOptions && textAdditionalOptions.addPaddings ? undefined : 0
    },
    icon: {
      color: color !== 'second' ? c.main : c.text,
      backgroundColor:
        iconAdditionalOptions && iconAdditionalOptions.showBg
          ? color === 'second'
            ? c.main
            : c.text
          : 'transparent',
      border: 'none',
      padding: iconAdditionalOptions && iconAdditionalOptions.addPadding ? 7 : 0,
      height: 'auto'
    }
  }

  const variantsHovered = {
    contained: {
      ...(isGradient
        ? { background: c.main, border: 'none' }
        : { backgroundColor: c.hover, border: `${borderStr} ${c.hover}` })
    },
    outlined: {
      color: c.hoverText,
      border: `${borderStr} ${c.hover}`
    },
    text: {
      color: c.hoverText
    },
    icon: {
      color: c.hoverText
    }
  }

  return {
    base: { ...base, ...variants[variant] },
    hover: variantsHovered[variant],
    svgColor: variants[variant].color
  }
}

const ButtonComponent: FC<Props> = ({
  disabled,
  label,
  classes,
  Icon,
  iconActive,
  size = 'md',
  color = 'primary',
  variant = 'contained',
  fill,
  optionsStyle,
  svg: SvgComponent,
  figure,
  style: propStyle,
  onClick,
  onKeyDown,
  to,
  type,
  ...rest
}) => {
  const [isHover, setHover] = useState(false)
  const { hover, base, svgColor } = buttonStyleSelector(color, variant, { optionsStyle })

  let iconProps: any = {}
  if (Icon && iconActive) iconProps = { iconActive: true }

  const isLink = Boolean(to)

  const handleClick = (e: any) => {
    if (disabled) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (onClick) onClick(e)
  }

  const handleKeyDown = (e: any) => {
    if (disabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (onKeyDown) onKeyDown(e)
  }

  const handleMouseEnter = () => {
    if (!disabled) setHover(true)
  }

  const handleMouseLeave = () => {
    setHover(false)
  }

  const style = {
    ...base,
    ...(isHover ? hover : {})
  }

  const className =
    `${classes.base} ${classes[size]} ${(rest as any).className || ''} ` +
    `${fill ? classes.fill : ''} ${disabled ? classes.disabled : ''} ` +
    `${figure === 'roundedSquere' ? classes.roundedSquere : ''}`

  // ---- LINK BRANCH ----
  if (isLink) {
    return (
      <Link
        {...rest as any}
        to={to as any}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-disabled={disabled ? true : undefined}
        tabIndex={disabled ? -1 : (rest as any).tabIndex}
        style={{ ...style, ...propStyle }}
        className={className}
      >
        {SvgComponent ? <SvgComponent color={svgColor} /> : null}
        {Icon ? <Icon {...iconProps} /> : null}
        {label ? label : null}
      </Link>
    )
  }

  // ---- BUTTON BRANCH ----
  return (
    <button
      {...rest}
      disabled={disabled}
      type={type ? type : 'button'}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-disabled={disabled ? true : undefined}
      tabIndex={disabled ? -1 : (rest as any).tabIndex}
      style={{ ...style, ...propStyle }}
      className={className}
    >
      {SvgComponent ? <SvgComponent color={svgColor} /> : null}
      {Icon ? <Icon {...iconProps} /> : null}
      {label ? label : null}
    </button>
  )
}

export const Button = memo(injectSheet(sheet)(ButtonComponent))
